// @ts-strict-ignore
import { Component, OnInit } from '@angular/core';
import { ValidatorFactory } from '@shared/validator.factory';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { MoveArticlePresenter } from '@project/components/article/presenter/move-article.presenter';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomValidators } from '@shared/custom-validators';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { SelectionModelService } from '../service/selection-model.service';
import { ArticleContainer } from '@3d-scene/model3d/article-container';

@Component({
  standalone: false,
  selector: 'app-move-article',
  templateUrl: './move-article.component.html',
})
export class MoveArticleComponent implements OnInit {
  public readonly MOVE_FROM_PARENT_ARTICLE_START: number = 1;
  public readonly MOVE_FROM_LAST_ARTICLE_BEFORE_SELECTION: number = 2;
  public readonly MOVE_BY_ARTICLE_POSITION: number = 3;

  public selectedOperation: number = 1;

  public formDistance: FormGroup;
  public formPosition: FormGroup;

  constructor(
    private formBuilder: FormBuilder,
    private bsModalRef: BsModalRef,
    private alertHelper: AlertHelper,
    private localeResourceManager: LocaleResourceManager,
    public presenter: MoveArticlePresenter,
    private selectionModelService: SelectionModelService,
    private validatorFactory: ValidatorFactory,
  ) {}

  public get initialized(): boolean {
    return this.presenter.initialized;
  }

  public get rangeOnLineArticleType(): string {
    return this.presenter.rangeOnLineArticleType;
  }
  public get lastArticleBeforeSelectionExists(): boolean {
    return this.presenter.lastArticleBeforeSelectionExists;
  }

  public getAllowedMovePositions(): number[] {
    return this.presenter.getAllowedMovePositions();
  }

  public ngOnInit(): void {
    const selectedArticleContainers: ArticleContainer[] = this.selectionModelService.getArticleContainers();
    this.presenter.initWithArticleContainers(selectedArticleContainers);
    if (!this.presenter || !this.presenter.initialized) {
      this.alertHelper
        .showError('moveArticlePopup.title.error_articles_to_move_not_found', 'moveArticlePopup.message.error_articles_to_move_not_found')
        .then(() => this.close());
      return;
    }
    this.formDistance = this.formBuilder.group({
      distance: [
        '0',
        Validators.compose([
          CustomValidators.trimRequired,
          this.validatorFactory.number({min: 0, max: Math.pow(2, 31) - 1}),
        ]),
      ],
    });
    this.formPosition = this.formBuilder.group({
      position: [
        '',
        Validators.compose([
          CustomValidators.trimRequired,
          CustomValidators.inAllowedNumbers(this.presenter.getAllowedMovePositions(), 'moveArticlePopup.message.error_unavailable_article_position'),
          this.validatorFactory.number({min: 1, max: this.presenter.getAllowedMaxPosition(), precision: 0}),
        ]),
      ],
    });
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public async nextButton_clickHandler(): Promise<void> {
    if (!this.validateForm()) {
      return;
    }
    const offsetValue: number = this.localeResourceManager.parseNumber(this.formDistance.controls.distance.value);
    const offsetIn3DUnits: number = UnitConverter.fromTo('mm', '3d', offsetValue);
    switch (this.selectedOperation) {
      case this.MOVE_FROM_PARENT_ARTICLE_START:
        await this.presenter.moveArticlesByRangeOnLineArticleStart(offsetIn3DUnits);
        break;
      case this.MOVE_FROM_LAST_ARTICLE_BEFORE_SELECTION:
        await this.presenter.moveArticlesByNeighbor(offsetIn3DUnits);
        break;
      case this.MOVE_BY_ARTICLE_POSITION:
        const position: number = this.localeResourceManager.parseNumber(this.formPosition.controls.position.value);
        await this.presenter.moveArticlesByPosition(position);
        break;
    }
    this.close();
  }

  public onSelectionChange(operation: number): void {
    this.selectedOperation = operation;
  }

  private validateForm(): boolean {
    let result: boolean = true;
    if (this.MOVE_BY_ARTICLE_POSITION === this.selectedOperation) {
      result = this.formPosition.valid;
    } else {
      result = this.formDistance.valid;
    }
    return result;
  }

  public getMoveFromStartRadioButtonLabel(articleType: string): string {
    let result: string;
    switch (articleType) {
      case ArticleTypeConstants.TYPE_N_BUSBAR:
        result = 'moveArticlePopup.label.move_from_busbar_start';
        break;
      case ArticleTypeConstants.TYPE_COVER:
        result = 'moveArticlePopup.label.move_from_cover_start';
        break;
      default:
        result = 'moveArticlePopup.label.move_from_mounting_rail_start';
        break;
    }
    return result;
  }

  public getMoveFromLastBeforeSelectionRadioButtonLabel(articleType: string): string {
    let result: string;
    switch (articleType) {
      case ArticleTypeConstants.TYPE_N_BUSBAR:
        result = 'moveArticlePopup.label.move_from_last_before_selection_on_busbar';
        break;
      case ArticleTypeConstants.TYPE_COVER:
        result = 'moveArticlePopup.label.move_from_last_before_selection_on_cover';
        break;
      default:
        result = 'moveArticlePopup.label.move_from_last_before_selection_on_rail';
        break;
    }
    return result;
  }
}
